import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CalendarEvent } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';
import { CalendarIcon } from '../components/icons';
import { notifyReminder } from '../mail/notify';
import { nextDue, remindersOf, standing, timesLabel, whenLabel, type Reminder } from './reminders';

/** How often the events are read again, for changes made elsewhere. */
const REFRESH_MS = 5 * 60_000;
/** How far ahead to look: the longest choice is a week before. */
const AHEAD_MS = 8 * 86_400_000;

const DISMISSED = 'zitimail.reminders.dismissed';
const NOTIFIED = 'zitimail.reminders.notified';

/** A set kept in this browser, each key until its event is over; shared by the open tabs. */
function stored(name: string, now: number): Record<string, number> {
  try {
    const all = JSON.parse(localStorage.getItem(name) ?? '{}') as Record<string, number>;
    return Object.fromEntries(Object.entries(all).filter(([, until]) => until > now));
  } catch {
    return {};
  }
}

function store(name: string, r: Reminder): void {
  try {
    const all = { ...stored(name, Date.now()), [r.key]: r.until };
    localStorage.setItem(name, JSON.stringify(all));
  } catch {
    // Private windows may refuse; the reminder shows again next load.
  }
}

/**
 * Raises event reminders while webmail is open, on every page: a card in the
 * corner until dismissed or the event is over, and a system notification
 * (browser or desktop app, mail/notify.ts) when webmail is not in front and
 * the device allows one. The phone apps raise their own.
 */
export function EventReminders({
  me,
  onOpen,
}: {
  me: string;
  onOpen: (event: CalendarEvent) => void;
}): JSX.Element | null {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(() => new Set(Object.keys(stored(DISMISSED, now))));

  const load = useCallback(() => {
    const at = Date.now();
    calendar
      .events(new Date(at - 86_400_000), new Date(at + AHEAD_MS))
      // Anything but a list (a proxy's error page) is no events, not a broken shell.
      .then((list) => setEvents(Array.isArray(list) ? list : []))
      // No calendar reachable: no reminders, and nothing to say about it here.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const every = window.setInterval(load, REFRESH_MS);
    const visible = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        load();
      }
    };
    const others = (e: StorageEvent) => {
      if (e.key === DISMISSED) {
        setDismissed(new Set(Object.keys(stored(DISMISSED, Date.now()))));
      }
    };
    window.addEventListener('genworx:calendar-changed', load);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('storage', others);
    return () => {
      window.clearInterval(every);
      window.removeEventListener('genworx:calendar-changed', load);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('storage', others);
    };
  }, [load]);

  const reminders = useMemo(
    () =>
      remindersOf(
        // An invitation declined is not a meeting to be reminded of.
        events.filter(
          (e) =>
            !e.attendees.some(
              (a) => a.email.toLowerCase() === me.toLowerCase() && a.partstat === 'DECLINED',
            ),
        ),
      ),
    [events, me],
  );

  // Wake at the next one due, and each minute for "Starts in …".
  useEffect(() => {
    const due = nextDue(reminders, now);
    const wait = Math.min(due === undefined ? 60_000 : due - now, 60_000);
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(wait, 250));
    return () => window.clearTimeout(timer);
  }, [reminders, now]);

  const shown = useMemo(() => standing(reminders, now, dismissed), [reminders, now, dismissed]);

  // The system notification, once per reminder in this browser.
  useEffect(() => {
    const told = stored(NOTIFIED, now);
    for (const r of shown) {
      if (told[r.key] !== undefined) {
        continue;
      }
      store(NOTIFIED, r);
      notifyReminder(
        r.event.title || '(no title)',
        `${whenLabel(r, now)}\n${timesLabel(r, now)}`,
        r.key,
        () => onOpen(r.event),
      );
    }
  }, [shown, now, onOpen]);

  if (shown.length === 0) {
    return null;
  }
  const dismiss = (r: Reminder) => {
    store(DISMISSED, r);
    setDismissed((s) => new Set([...s, r.key]));
  };
  return (
    <section className="reminders" aria-label="Reminders" aria-live="polite">
      {shown.slice(0, 3).map((r) => (
        <article key={r.key} className="reminder">
          <span className="reminder-icon" aria-hidden="true">
            <CalendarIcon />
          </span>
          <div className="reminder-text">
            <strong>{r.event.title || '(no title)'}</strong>
            <span className="reminder-when">{whenLabel(r, now)}</span>
            <span className="reminder-times">{timesLabel(r, now)}</span>
          </div>
          <div className="reminder-actions">
            <button
              type="button"
              onClick={() => {
                onOpen(r.event);
                dismiss(r);
              }}
            >
              Open
            </button>
            <button type="button" onClick={() => dismiss(r)}>
              Dismiss
            </button>
          </div>
        </article>
      ))}
      {shown.length > 3 && (
        <p className="reminder-more">
          {shown.length - 3} more{' '}
          <button type="button" onClick={() => shown.forEach(dismiss)}>
            Dismiss all
          </button>
        </p>
      )}
    </section>
  );
}
